import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
  Checkbox,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import authService from '../../services/authService';

const UserManagementPage = () => {
  const { user: currentUser } = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role_name: 'user',
    country: '',
    city: '',
    phone_number: '',
    company_size: '',
  });

  // Get allowed roles based on organization type
  const getAllowedRoles = () => {
    if (!currentUser?.organization) return [];
    const orgType = currentUser.organization.type;
    if (orgType === 'University') {
      return ['admin', 'professor', 'student', 'financial_admin', 'user'];
    } else if (orgType === 'Municipality' || orgType === 'Geopark') {
      return ['admin', 'financial_admin', 'user'];
    }
    return ['user'];
  };

  const allowedRoles = getAllowedRoles();

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) {
      return users;
    }
    const query = searchQuery.toLowerCase().trim();
    return users.filter(
      (user) =>
        (user.first_name?.toLowerCase().includes(query) ||
          user.last_name?.toLowerCase().includes(query))
    );
  }, [users, searchQuery]);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const userList = await authService.getOrganizationUsers();
      setUsers(userList);
    } catch (err) {
      console.error('Failed to load users:', err);
      setError('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      setError('Please enter an email address');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await authService.inviteUser(inviteEmail.trim(), inviteRole);
      setSuccess('Invitation sent successfully! The user will receive an email with accept/deny links.');
      setInviteEmail('');
      setInviteRole('user');
      setShowInviteDialog(false);
      // Reload users list
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await authService.createOrganizationUser(formData);
      setSuccess('User created successfully! They will receive an email to set their password.');
      // Reset form
      setFormData({
        email: '',
        password: '',
        first_name: '',
        last_name: '',
        role_name: 'user',
        country: '',
        city: '',
        phone_number: '',
        company_size: '',
      });
      // Reload users list
      await loadUsers();
      // Close modal on success
      setShowCreateDialog(false);
    } catch (err) {
      setError(err.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseCreateDialog = () => {
    setShowCreateDialog(false);
    // Reset form when closing
    setFormData({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role_name: 'user',
      country: '',
      city: '',
      phone_number: '',
      company_size: '',
    });
    setError('');
    setSuccess('');
  };

  const handleRemoveUsers = async () => {
    if (selectedUserIds.length === 0) {
      setError('Please select at least one user to remove');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await authService.removeUsers(selectedUserIds);
      setSuccess(`Successfully removed ${selectedUserIds.length} user(s) from the organization.`);
      setSelectedUserIds([]);
      setShowRemoveConfirm(false);
      // Reload users list
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Failed to remove users');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUserIds.length === filteredUsers.length) {
      setSelectedUserIds([]);
    } else {
      setSelectedUserIds(filteredUsers.map((user) => user.id));
    }
  };

  if (!currentUser?.organization) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          You must belong to an organization to manage users.
        </Alert>
      </Container>
    );
  }

  if (currentUser?.role?.name !== 'admin') {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Only organization administrators can manage users.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          User Management
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage users for {currentUser.organization?.name}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Organization Users Section */}
        <Typography variant="h5" component="h2" sx={{ mb: 2 }}>
          Organization Users
        </Typography>

        {/* Search Input */}
        <TextField
          fullWidth
          placeholder="Search by first or last name"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton edge="start">
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {loadingUsers ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer
            sx={{
              maxHeight: 'calc(100vh - 400px)',
              overflow: 'auto',
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < filteredUsers.length}
                      checked={filteredUsers.length > 0 && selectedUserIds.length === filteredUsers.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>City</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {searchQuery ? 'No users found matching your search' : 'No users found'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedUserIds.includes(user.id)}
                          onChange={() => handleSelectUser(user.id)}
                        />
                      </TableCell>
                      <TableCell>
                        {user.first_name} {user.last_name}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        {user.role?.name
                          ? user.role.name.charAt(0).toUpperCase() +
                            user.role.name.slice(1).replace('_', ' ')
                          : '-'}
                      </TableCell>
                      <TableCell>{user.country || '-'}</TableCell>
                      <TableCell>{user.city || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Action Buttons at Bottom */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            color="error"
            onClick={() => {
              if (selectedUserIds.length === 0) {
                setError('Please select at least one user to remove');
                return;
              }
              setShowRemoveConfirm(true);
            }}
            disabled={selectedUserIds.length === 0 || loading}
          >
            Remove Account{selectedUserIds.length > 0 ? ` (${selectedUserIds.length})` : ''}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => setShowInviteDialog(true)}
            disabled={loading}
          >
            Invite User
          </Button>
          <Button
            variant="contained"
            onClick={() => setShowCreateDialog(true)}
            disabled={loading}
          >
            Create User
          </Button>
        </Box>

        {/* Invite User Dialog */}
        <Dialog
          open={showInviteDialog}
          onClose={() => {
            setShowInviteDialog(false);
            setInviteEmail('');
            setInviteRole('user');
            setError('');
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Invite User to Organization</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="user@example.com"
              sx={{ mt: 2 }}
              required
              autoFocus
            />
            <TextField
              fullWidth
              select
              label="Role"
              name="role_name"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
              sx={{ mt: 2 }}
              required
            >
              {allowedRoles.map((role) => (
                <MenuItem key={role} value={role}>
                  {role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' ')}
                </MenuItem>
              ))}
            </TextField>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              The user will receive an email with a link to accept or decline the invitation.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowInviteDialog(false);
                setInviteEmail('');
                setInviteRole('user');
                setError('');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleInviteUser}
              variant="contained"
              disabled={loading || !inviteEmail.trim()}
            >
              {loading ? <CircularProgress size={24} /> : 'Send Invitation'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create User Dialog */}
        <Dialog
          open={showCreateDialog}
          onClose={handleCloseCreateDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Create New User</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                {success}
              </Alert>
            )}

            <Box component="form" ref={formRef} onSubmit={handleCreateUser} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    inputProps={{ minLength: 8 }}
                    helperText="User will be required to change this password on first login"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Role"
                    name="role_name"
                    value={formData.role_name}
                    onChange={handleChange}
                    required
                  >
                    {allowedRoles.map((role) => (
                      <MenuItem key={role} value={role}>
                        {role.charAt(0).toUpperCase() + role.slice(1).replace('_', ' ')}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone_number"
                    value={formData.phone_number}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Country"
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Company Size"
                    name="company_size"
                    value={formData.company_size}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseCreateDialog}>Cancel</Button>
            <Button
              onClick={() => formRef.current?.requestSubmit()}
              variant="contained"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Create User'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Remove Users Confirmation Dialog */}
        <Dialog
          open={showRemoveConfirm}
          onClose={() => setShowRemoveConfirm(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Removal</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to remove {selectedUserIds.length} user(s) from the organization?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This action cannot be undone. The users will no longer have access to the organization.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRemoveConfirm(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleRemoveUsers}
              variant="contained"
              color="error"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default UserManagementPage;
